import { useEffect, type ReactNode } from "react";
import { useUIStore } from "@/store/useUIStore";
import { applyBranding } from "@/lib/branding";

/**
 * Pushes the stored palette onto <html> as --brand-* custom properties.
 *
 * It subscribes to the store IMPERATIVELY rather than with a selector hook on purpose.
 * A color input fires continuously while its swatch is dragged; a hook subscription
 * would re-render this provider on every frame of that drag, and it sits above the
 * entire routed tree. Reading the store outside React means a drag costs ten style
 * property writes per frame and zero React renders.
 *
 * index.html runs the same write before first paint, so this is the ongoing sync, not
 * the initial one -- but it still applies once on mount to cover the case where the
 * persisted value is rehydrated after that script ran.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    const root = document.documentElement;
    applyBranding(root, useUIStore.getState().branding);
    return useUIStore.subscribe((state, prev) => {
      if (state.branding !== prev.branding) applyBranding(root, state.branding);
    });
  }, []);

  return <>{children}</>;
}
